import React, {
  Fragment,
  useEffect,
  useRef,
  useState,
  useCallback,
  useContext, // Aunque useAppContext ya lo hace
} from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useRouter } from "next/router";
import { AppContext, useAppContext } from "../../context/state"; // AppContext no parece usarse directamente
import apiService from "../../services/apiService";
import dynamic from "next/dynamic";
import { useUser } from "@auth0/nextjs-auth0/client";
import { onAuthStateChanged, signInWithCustomToken } from "firebase/auth";
import { auth } from "../../pages"; // Asumiendo que aquí se exporta la instancia auth de Firebase inicializada
import axios from "axios";
import { Button } from "../Common/Button";
import * as amplitude from "@amplitude/analytics-browser"; // Si usas Amplitude
import { toast } from "react-toastify";
import { UnlockIcon } from "lucide-react";
import { trackEvent } from "../../Utils/trackEvent"; // Importa trackEvent

// --- Icono de Carga (Ejemplo con VscLoading, ajusta si usas otro) ---
import { loadIcons } from "../../Utils/iconUtils";

// --- Componentes Cargados Dinámicamente ---
// Esto ayuda a reducir el bundle inicial
const SessionInfo = dynamic(() => import("../Drawer/sessionInfo"));
const MobileBar = dynamic(() => import("../MobileBar"));
const MobileTopBar = dynamic(() => import("../MobileTopBar"));
const Sidebar = dynamic(() => import("../Sidebar"));
const Topbar = dynamic(() => import("../Topbar"));
const PrefetchIcons = dynamic(() => import("../PrefetchIcons")); // Si lo sigues usando

// ========================================================================
// Componente DashboardLayout Reestructurado
// ========================================================================
console.log('DashboardLayout: Component rendering');

// Componente de carga para los iconos
const IconLoader = () => (
  <span className="inline-block h-4 w-4 animate-pulse rounded-full bg-gray-200"></span>
);

const DashboardLayout = ({ children }) => {
  // --- Hooks Principales ---
  console.log('DashboardLayout: useUser hook calling');
  const router = useRouter();
  const { user: session, error: authError, isLoading: isAuthLoading } = useUser();
  console.log('DashboardLayout: useUser state:', { isAuthLoading, session: !!session, authError });
  
  // Estado para almacenar los iconos cargados dinámicamente
  const [Icons, setIcons] = useState({
    loading: true,
    RiDeleteBin6Line: null,
    VscLoading: null,
  });
  // Contexto de la App
  const {
    isSessionInspection,
    setIsSessionInspection,
    isSidebarOpen, // Usado para el padding del main
    isOverlayActive,
    setOverlayActive,
    sessionData, // Usado en deleteInspection y pasado a SessionInfo
    setSessionData,
    isDeleting,
    setIsDeleting,
    associatedInspections, // Usado en deleteInspection
    setAssociatedInspections,
    isPaywallOpen,
    setIsPaywallOpen,
  } = useAppContext();

  // --- Estado Local ---
  const [isMobile, setIsMobile] = useState(false); // Inicializa en false, el useEffect lo corregirá
  const [previousUser, setPreviousUser] = useState(null); // Para el listener de Firebase Auth
  const cancelButtonRef = useRef(null); // Para el foco en modales

  // Cargar los iconos necesarios dinámicamente
  const loadIconsAsync = async () => {
    try {
      setIcons((prev) => ({ ...prev, loading: true }));

      const iconConfigs = [
        { family: "ri", name: "RiDeleteBin6Line" },
        { family: "vsc", name: "VscLoading" },
      ];

      const loadedIcons = await loadIcons(iconConfigs);

      setIcons({
        loading: false,
        ...loadedIcons,
      });
    } catch (error) {
      console.error("Error cargando iconos:", error);
      setIcons((prev) => ({ ...prev, loading: false }));
    }
  };

  // Cargar los iconos necesarios dinámicamente
  useEffect(() => {
    loadIconsAsync();
  }, []);

  // --- Efecto 1: Guardia de Autenticación Auth0 (Centralizado) ---
  useEffect(() => {
    console.log('DashboardLayout: Auth guard useEffect running', { isAuthLoading, session: !!session, authError });
    // Solo actuar cuando la verificación de Auth0 haya terminado
    if (!isAuthLoading) {
      console.log('DashboardLayout: Auth guard - Auth0 loading finished');
      if (authError) {
        // Error durante la verificación de Auth0
        console.error("DashboardLayout: Auth0 authentication error:", authError);
        toast.error("Error de autenticación. Intenta recargar.", { autoClose: false });
      } else if (!session) {
        // Usuario NO autenticado en Auth0 -> Redirigir a login
        console.log("DashboardLayout: User not authenticated, redirecting to login...");
        const returnTo = encodeURIComponent(router.asPath);
        console.log(`DashboardLayout: Redirecting to /api/auth/login?returnTo=${returnTo}`);
        router.replace(`/api/auth/login?returnTo=${returnTo}`);
      } else {
        console.log("DashboardLayout: User authenticated, proceeding.");
      }
    } else {
      console.log("DashboardLayout: Auth guard - Waiting for Auth0 loading to finish...");
    }
  }, [isAuthLoading, session, authError, router]);

  // --- Efecto 2: Sincronización con Firebase Auth ---
  // Función para iniciar sesión en Firebase con el token personalizado
  const handleFirebaseSignIn = useCallback(async (uid) => {
    // Evitar llamadas si no hay UID
    if (!uid) {
      console.warn("Intento de login en Firebase sin UID.");
      return;
    }
    try {
      console.log(`Solicitando token de Firebase para UID: ${uid}`);
      const firebaseResponse = await axios.post(`/api/fireauth`, { uid });
      if (firebaseResponse.data?.token) {
        console.log("Token de Firebase recibido, intentando signInWithCustomToken...");
        await signInWithCustomToken(auth, firebaseResponse.data.token);
        console.log("Login en Firebase exitoso.");
      } else {
        console.error("No se recibió token en la respuesta de /api/fireauth");
      }
    } catch (error) {
      console.error("Error durante el proceso de login en Firebase:", error.message, error.response?.data);
      // Considerar qué hacer aquí, ¿reintentar? ¿mostrar error?
    }
  }, []); // useCallback sin dependencias porque usa 'auth' global y recibe 'uid'

  // Listener para cambios de estado en Firebase Auth (maneja deslogueo / primer login)
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      // console.log("Firebase Auth State Changed:", firebaseUser ? `User ${firebaseUser.uid}` : "No user");
      if (!firebaseUser && !isAuthLoading && session?.sub) {
        // Si Firebase se desloguea PERO Auth0 sigue logueado (y ya cargó), re-intentar login Firebase
        console.log("Usuario deslogueado de Firebase pero logueado en Auth0. Reintentando login Firebase...");
        handleFirebaseSignIn(session.sub);
      }
      // Actualizar previousUser solo si realmente cambia para evitar bucles
      setPreviousUser((prev) => (prev?.uid !== firebaseUser?.uid ? firebaseUser : prev));
    });

    return () => unsubscribe(); // Limpiar listener al desmontar
  }, [session, isAuthLoading, handleFirebaseSignIn]); // Depende de la sesión Auth0 y el estado de carga

  // Efecto para iniciar sesión en Firebase cuando la sesión Auth0 esté lista por primera vez
  useEffect(() => {
    // Asegurarse que Auth0 no esté cargando, que haya sesión y un UID
    if (!isAuthLoading && session?.sub) {
        // Comprobar si ya hay un usuario en Firebase Auth para no loguear innecesariamente
        if (!auth.currentUser || auth.currentUser.uid !== session.sub) {
            // console.log("Sesión Auth0 cargada y diferente de Firebase Auth actual. Llamando a handleFirebaseSignIn...");
            handleFirebaseSignIn(session.sub);
        } else {
            // console.log("Sesión Auth0 cargada y coincide con Firebase Auth actual. No se necesita login Firebase.");
        }
    }
  }, [session, isAuthLoading, handleFirebaseSignIn]); // Dependencias clave


  // --- Efecto 3: Detectar tamaño de pantalla (Mobile/Desktop) ---
  useEffect(() => {
    const handleResize = () => {
      // Ajusta el umbral si es necesario (1400 parece un buen punto para layouts diferentes)
      setIsMobile(window.innerWidth < 1400);
    };
    handleResize(); // Ejecutar al montar
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize); // Limpiar listener
  }, []);

  // --- Callbacks y Handlers ---
  const handleUpgrade = useCallback(() => {
    trackEvent("Click Paywall Open", { page: router.pathname });
    setIsPaywallOpen(false); // Cerrar modal antes de redirigir
    router.push("/home/welcome/paywall");
  }, [router, setIsPaywallOpen]); // Añadir setIsPaywallOpen a dependencias

  // Asegúrate que todas las dependencias de useCallback estén listadas
  const deleteInspection = useCallback(async () => {
    if (!sessionData?.roomId) { // Comprobación más segura
      toast.error("Error: No se pudo encontrar el ID de la visita.");
      return;
    }

    setIsDeleting(true);

    try {
      trackEvent("Click Visita Eliminar", {
        visitName: sessionData.sessionName,
        roomId: sessionData.roomId,
      });

      // Guardar ID antes de limpiar sessionData si es necesario
      const roomIdToDelete = sessionData.roomId;

      await apiService.deleteInspection(roomIdToDelete);

      // Limpiar y cerrar modal ANTES de actualizar lista (puede evitar errores visuales)
      setSessionData({});
      setOverlayActive(false);

      // Actualizar lista de inspecciones en el contexto
      setAssociatedInspections((prevInspections) =>
         prevInspections.filter((inspection) => inspection.roomId !== roomIdToDelete)
      );

      toast.success("Visita eliminada con éxito.");

    } catch (error) {
      console.error("Error deleting inspection:", error);
      toast.error(error.message || "No se pudo eliminar la visita. Inténtalo nuevamente.");
    } finally {
      setIsDeleting(false);
    }
  // Dependencias: todas las variables/funciones externas usadas
  }, [sessionData, setSessionData, setOverlayActive, setIsDeleting, setAssociatedInspections]);


  // --- Lógica de Renderizado ---
  console.log('DashboardLayout: Evaluating render state', { isAuthLoading, session: !!session, authError });

  // 1. Estado de Carga Principal (Esperando Auth0)
  if (isAuthLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-gray-100">
        {/* Usar VscLoading u otro loader consistente */}
        {Icons.VscLoading ? (
          <Icons.VscLoading className="h-10 w-10 animate-spin text-plaved-500" />
        ) : (
          <IconLoader />
        )}
        <p className="ml-4 text-lg text-gray-600">Cargando...</p>
      </div>
    );
  }

  // 2. Estado de Error de Auth0 o Sin Sesión (Redirección en curso)
  // El useEffect ya se encarga de la redirección si !session.
  // Devolver null evita renderizar brevemente el layout vacío mientras redirige.
  // Si hubo un error, podríamos mostrar un mensaje aquí.
  if (!session || authError) {
     // El useEffect ya está redirigiendo si !session
     // Podríamos mostrar un mensaje específico si hay authError
     if (authError) {
         return (
            <div className="flex h-screen w-full items-center justify-center bg-gray-100">
                <p className="text-lg text-red-600">Error de autenticación.</p>
            </div>
         );
     }
     // Mientras redirige si !session, devolver null o el loader es lo más limpio
     return null;
  }

  // 3. Usuario Autenticado -> Renderizar el Layout completo y los hijos
  return (
    <div className="flex w-full">
      {/* Componentes que no dependen del layout mobile/desktop */}
      <PrefetchIcons />

      {/* Drawer para Info de Sesión (Inspección) */}
      {isSessionInspection && (
        <SessionInfo
          isOpen={isSessionInspection}
          setIsOpen={setIsSessionInspection}
          desiredProject={sessionData} // Asegúrate que sessionData contenga lo necesario para SessionInfo
        />
      )}

      {/* Modal de Confirmación para Eliminar */}
      <Transition.Root show={isOverlayActive} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-10"
          initialFocus={cancelButtonRef}
          onClose={() => setOverlayActive(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                  <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                  <div className=" space-y-2">
                    <div className="flex items-start gap-2">
                      {Icons.RiDeleteBin6Line ? (
                        <Icons.RiDeleteBin6Line
                          className="mt-1 h-6  w-6 text-black"
                          aria-hidden="true"
                        />
                      ) : (
                        <IconLoader />
                      )}

                      <Dialog.Title
                        as="h3"
                        className="mb-1 text-left text-[20px] font-medium "
                      >
                        ¿Seguro que quieres eliminar esta visita?
                      </Dialog.Title>
                    </div>
                    <p className="text-left text-[14px] font-light text-plaved-5 ">
                      Toda la información será eliminada permanentemente. Esta
                      acción es IRREVERSIBLE.
                    </p>
                  </div>
                  <div className="mt-4 flex justify-center gap-2 ">
                    <Button
                      onClick={() => setOverlayActive(false)}
                      ref={cancelButtonRef}
                      tertiary
                      full
                      // Quitado isLoading, el botón Cancelar no debería mostrar carga
                    >
                      Cancelar
                    </Button>
                    <Button
                      primary
                      onClick={deleteInspection}
                      type="button"
                      className="bg-red-500 hover:bg-red-600" // Añadir hover state
                      full
                      lg
                      isLoading={isDeleting} // Mostrar estado de carga aquí
                      disabled={isDeleting} // Deshabilitar mientras se elimina
                    >
                      {/* Mostrar texto o loader basado en isDeleting */}
                      {isDeleting ? "Eliminando..." : "Eliminar"}
                    </Button>
                  </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Modal de Paywall */}
      <Transition.Root show={isPaywallOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[100]"
          onClose={() => setIsPaywallOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-[101] overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md">
                  <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                  <Dialog.Title className="text-lg font-medium text-gray-900">
                    Funcionalidad Bloqueada
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Has llegado al límite de tu cuenta. Para continuar, debes subir tu cuenta al Plan Professional.
                    </p>
                  </div>
                  <div className="mt-4 flex justify-center">
                    <Button
                      onClick={() => setIsPaywallOpen(false)}
                      className="mr-2"
                      sm
                      tertiary // Quizá tertiary para Cancelar
                    >
                      Cancelar
                    </Button>
                    <Button sm primary onClick={handleUpgrade}>
                      Desbloquear Ahora <UnlockIcon className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Renderizado condicional del Layout Mobile vs Desktop */}
      {isMobile === null ? (
        // Puedes mostrar un loader brevemente mientras se determina isMobile si lo prefieres
        <div className="flex h-screen w-full items-center justify-center">Cargando layout...</div>
      ) : isMobile ? (
        // --- Layout Mobile ---
        <>
          {/* Pasar 'session' (alias de user) si MobileTopBar lo necesita */}
          <MobileTopBar session={session} />
          <main className="w-full">{children}</main> {/* Contenido de la página */}
          <MobileBar />
        </>
      ) : (
        // --- Layout Desktop ---
        <>
          {/* Pasar 'session' (alias de user) si Topbar lo necesita */}
          <Topbar session={session} />
           {/* El padding del main se ajusta basado en si el Sidebar está abierto (estado del contexto) */}
          <main className="w-full transition-all duration-300 ease-in-out">
            <div className={`${!isSidebarOpen ? "pl-[80px]" : "pl-[220px]"}`}>
              {children} {/* Contenido de la página */}
            </div>
          </main>
           {/* Pasar 'session' (alias de user) si Sidebar lo necesita */}
          <Sidebar session={session} />
        </>
      )}
    </div>
  );
};

export default DashboardLayout;